import Game from '../Game';
import { Random } from './Random';
import { DeprecatedQuests, DeprecatedQuestType, Quests, QuestType } from '../State/Player/QuestType';
import { IPlayerExpireableQuestData, IPlayerState, IPlayerStateQuestData } from '../State/Player/IPlayerState';
import { Immutable, isNull } from './Types';
import { IRewardData } from './RewardHelper';
import DateHelper from './DateHelper';
import { questTimerDuration } from '../Constants/Quests';
import { MathHelper } from './MathHelper';
import { tVector2 } from './ArrayHelper';
import { hash } from './HashString';
import { resetEventHandlerData, track, TrackMethod } from './Tracking/Track';
import { QuestEventName } from './Tracking/TrackingEvent';
import { TActionType, TGameArea, TransactionType } from './Tracking/DataSet/Transaction';
import { trackResourceEvent } from './Tracking/TrackingHelpers/Transaction';
import { isWebPSupported } from './WebPFunctions';
import { GameJSON } from 'GameJSON';
import { IGameState } from 'State/Game/IGameState';
import { report } from 'report';
import { IEventStateData } from 'State/Events/IEventsState';
import { EventType } from './Helpers/Events/EventType';
import { isArray } from 'lodash';
import { cached } from './async';
import { isEventType } from 'State/Events/EventHelper';

export interface IQuestsConfig {
    quests: Random.IWeightedItem<IQuestData>[];
    /**
     * Rewards is an array that consist of either;
     * - A single set of reward data that provides the required style data and reward amounts
     * - Or an array of reward data to provide support for multi stage quests
     * --Index of the reward is directly related to the tier of the quest
     */
    rewards: Array<IQuestRewardData | IQuestRewardData[]>;
}

interface IQuestRewardData extends IRewardData {
    iconCacheKey: string;
    iconScale: tVector2;
    textLocalizationKey?: string;
    textStyle?: string;
    angle?: number;
}

interface IQuestData {
    type: QuestType;
    /**
     * Targets is an array that consist of either;
     * - A single target number that indicates the required progress to complete the quest
     * - Or an array of targets to provide support for multi stage quests
     * --Index of the target is directly related to the tier of the quest
     */
    targets: Array<number[] | number>;
    backgrounds: string[];
}

export type QuestStateKeys = keyof Pick<IPlayerState, 'eventQuests' | 'quests'>;

export default class QuestHelper {
    private static readonly QuestConfigKey = 'quests';

    public static trackNewQuest(state: IGameState, quest: IPlayerStateQuestData): void {
        try {
            track('qQuestName', quest.type, TrackMethod.SET);
            track('qQuestID', quest.id, TrackMethod.SET);
            track('qQuestTier', quest.tier, TrackMethod.SET);
            resetEventHandlerData(QuestEventName.questStart, state);
        } catch (error) {
            report('Failed to track new quest', {
                error,
                once: true,
                sampleRate: 0.1,
                tags: {
                    type: quest?.type,
                    id: quest?.id,
                    tier: quest?.tier,
                },
                fingerprint: 'trackNewQuest:FailedToTrack',
                state,
            });
        }
    }

    public static trackCompletedQuest(quest: IPlayerStateQuestData): void {
        track('tGameArea', TGameArea.QUEST_COMPLETE);
        track('transactionType', TransactionType.REWARD);
        track('tActionType', TActionType.REWARD);
        track('tQuestName', quest.type);
        track('tQuestID', quest.id);
        track('tQuestTier', quest.tier);
        track('tLevelContext', undefined);
        trackResourceEvent(Game.store.getState());
    }

    public static getNewQuestAndTrack(
        state: IGameState,
        currentQuests: Immutable<IPlayerStateQuestData[]>
    ): IPlayerStateQuestData {
        const quest: IPlayerStateQuestData = QuestHelper.getNewQuest({ currentQuests });
        QuestHelper.trackNewQuest(state, quest);
        return quest;
    }

    public static getNewEventQuestAndTrack(
        state: IGameState,
        eventData: IEventStateData
    ): IPlayerExpireableQuestData {
        const startDate = new Date(eventData.startTime ?? 0).getTime();

        const quest: IPlayerExpireableQuestData = {
            ...QuestHelper.getNewQuest({ questPoolKey: QuestHelper.getQuestPoolKeyByEventType(eventData.type), forcedTier: 0 }),
            expiration: new Date(startDate + eventData.duration),
            eventType: eventData.type,
            indexOfLastRewardCollected: null,
        };
        //QuestHelper.trackNewQuest(state, quest);
        return quest;
    }

    public static getQuestPoolKeyByEventType(eventType: EventType): string {
        return eventType ? `${eventType}_quests` : QuestHelper.QuestConfigKey;
    }

    /**
     * Gets new quests ignoring all quests types that are currently active
     */
    public static getNewQuest(options: {
        currentQuests?: Immutable<IPlayerStateQuestData[]>;
        forcedQuestType?: QuestType;
        forcedTier?: number;
        questPoolKey?: string;
    }): IPlayerStateQuestData {
        const { currentQuests = [], forcedTier = null, questPoolKey } = options;
        let { forcedQuestType } = options;

        const { quests } = QuestHelper.getQuestConfig(questPoolKey);
        let newQuest: IQuestData;

        // Check if questType to force is valid (Should only happen when using cheats)
        if (forcedQuestType && !Object.values(Quests).includes(forcedQuestType)) {
            // Notify testers when they tried an invalid questType
            console.warn(`The questType '${forcedQuestType}' is invalid, a random quest has been chosen instead`);
            forcedQuestType = undefined;
        }

        if (forcedQuestType) {
            newQuest = quests.find(weightedQuestData => weightedQuestData.data.type === forcedQuestType).data;
        } else {
            const activeQuestTypes = currentQuests.map(quest => quest.type);

            const filteredQuests = quests.filter(quest => !activeQuestTypes.includes(quest.data.type));
            newQuest = Random.getRandomWeighted(filteredQuests);
        }

        let tier: number;
        if (forcedTier !== null) {
            tier = MathHelper.clamp(forcedTier, 0, getMaxTier());
        } else {
            tier = Random.int(0, getMaxTier());
        }

        const result: IPlayerStateQuestData = {
            id: (Date.now() + hash(newQuest.type)).toString(),
            type: newQuest.type,
            cooldownStartedAt: null,
            progress: 0,
            tier,
        };

        return result;

        function getMaxTier(): number {
            return newQuest.targets.length - 1;
        }
    }

    public static getQuestRewards(tier: number, questPoolKey?: string): Array<IRewardData | IRewardData[]> {
        const { rewards } = QuestHelper.getQuestConfig(questPoolKey);
        const tierRewards = rewards[tier];
        return isArray(tierRewards) ? tierRewards : [tierRewards];
    }

    private static getQuestConfig(questPoolKey?: string): IQuestsConfig {
        const key = questPoolKey && GameJSON[questPoolKey] ? questPoolKey : QuestHelper.QuestConfigKey;
        return JSON.parse(GameJSON[key]);
    }

    public static canCompleteQuest(quest?: Immutable<IPlayerStateQuestData>): boolean {
        // Can't be completed if it doesn't exist or is already completed
        if (!quest || quest.cooldownStartedAt !== null) return false;

        const questTarget = QuestHelper.getQuestTarget(
            quest.type,
            quest.tier,
            QuestHelper.getQuestPoolKeyByEventType(quest.eventType)
        );
        // If quest has multiple targets check if a new target after the last claimed reward has been met
        if (isArray(questTarget)) {
            return questTarget.some(
                (target, index) => index > (quest.indexOfLastRewardCollected ?? -1) && quest.progress >= target
            );
        }

        // Else check if target has been met
        return quest.progress >= questTarget;
    }

    public static getQuestTarget(questType: QuestType, tier: number, questPoolKey?: string): number | number[] {
        const { quests } = QuestHelper.getQuestConfig(questPoolKey);
        const requestedQuest = quests.find(quest => quest.data.type === questType);
        return requestedQuest?.data.targets[tier];
    }

    public static getNextQuestTarget(
        quest: Immutable<IPlayerStateQuestData>,
        options: {
            defaultToHighestTarget: boolean;
            questPoolKey?: string;
        }
    ): number {
        const { defaultToHighestTarget = true, questPoolKey } = options;
        const questTarget = QuestHelper.getQuestTarget(quest.type, quest.tier, questPoolKey);
        if (isArray(questTarget)) {
            const nextUnclaimedTarget = questTarget[(quest.indexOfLastRewardCollected ?? -1) + 1];

            return nextUnclaimedTarget === undefined && defaultToHighestTarget
                ? questTarget[questTarget.length - 1]
                : nextUnclaimedTarget;
        }

        return questTarget;
    }

    public static getFinalQuestTarget(type: QuestType, tier: number, questPoolKey?: string): number {
        const questTarget = QuestHelper.getQuestTarget(type, tier, questPoolKey);
        return isArray(questTarget) ? questTarget[questTarget.length - 1] : questTarget;
    }

    public static hasQuestTargetBeenClaimed(quest: Immutable<IPlayerStateQuestData>, targetIndex: number): boolean {
        const questTarget = QuestHelper.getQuestTarget(
            quest.type,
            quest.tier,
            QuestHelper.getQuestPoolKeyByEventType(quest.eventType)
        );
        if (!isArray(questTarget)) {
            return false;
        }
        return (quest.indexOfLastRewardCollected ?? -1) >= targetIndex;
    }

    public static getQuestMaxTier(questType: QuestType, questPoolKey?: string): number {
        const { quests } = QuestHelper.getQuestConfig(questPoolKey);
        const requestedQuest = quests.find(quest => quest.data.type === questType);

        return requestedQuest?.data.targets.length - 1;
    }

    public static isCooldownCompleted(cooldownStartedAt: Immutable<Date>): boolean {
        return DateHelper.timeSince(cooldownStartedAt) >= questTimerDuration;
    }

    public static getBackgroundTileKey(questType: QuestType, tier: number, questPoolKey?: string): string {
        const { quests } = QuestHelper.getQuestConfig(questPoolKey);
        const requestedQuest = quests.find(quest => quest.data.type === questType);
        return (
            requestedQuest?.data.backgrounds[
                MathHelper.clamp(tier, 0, requestedQuest.data.backgrounds.length - 1)
                // This cannot use getImageExtensionToLoad()
            ] + (isWebPSupported() ? '.webp' : '')
        );
    }

    public static getTitleKey(questType: QuestType): string {
        return `QuestTitle_${questType}`;
    }

    public static getNotificationKey(questType: QuestType): string {
        return `QuestNotification_${questType}`;
    }

    public static addTileIfNeeded(questType: QuestType, tier: number, questPoolKey?: string): void {
        const key = QuestHelper.getBackgroundTileKey(questType, tier, questPoolKey);
        if (key && !Game.resource.existsAndLoaded(key)) {
            Game.resource.add(key, `resource/graphics/quests/${key}`);
        }
    }

    public static isQuestOnCooldown(data: Immutable<IPlayerStateQuestData>) {
        return data.cooldownStartedAt !== null;
    }

    public static isDeprecatedQuest(questType: QuestType): boolean {
        return Object.values(DeprecatedQuests).includes(questType as DeprecatedQuestType);
    }

    /**
     * Parses questType; Will throw error if not
     * @returns Quest type or null if no type is assigned
     */
    public static parseQuestType(data: unknown): QuestType | null {
        if (isNull(data) || this.isQuestType(data)) return data;

        throw new Error('Unexpected quest type');
    }

    public static isQuestType(data: unknown): data is QuestType {
        return CACHED_QUEST_TYPES().includes(data as QuestType);
    }

    /**
     * Parses quest cooldown start time; Will throw error if not
     */
    public static parseCooldownStartedAt(data: unknown): Date | null {
        if (data === null || typeof data !== 'number' || Math.floor(data) !== data || data < 0) return null;

        // Else return new date
        return new Date(data);
    }

    /**
     * Parses quest progress, will throw an error if invalid and will not parse if quest is not active (as there should be no progress to parse)
     */
    public static parseQuestProgress(data: unknown): number {
        if (typeof data === 'number') {
            return data;
        }
        return 0;
    }

    /**
     * Parses quest tier, will throw an error if invalid and will not parse if quest is not active (as there should be no tier to parse)
     */
    public static parseQuestTier(data: unknown, type: QuestType): number {
        // if tier is not a positive integer or is greater than the max tier then throw error
        if (
            typeof data !== 'number' ||
            Math.floor(data) !== data ||
            data < 0 ||
            data > QuestHelper.getQuestMaxTier(type)
        ) {
            return null;
        }

        return data;
    }

    /**
     * Parses quest id, will throw an error if not a string
     */
    public static parseQuestId(data: unknown): string {
        if (typeof data !== 'string') {
            throw new Error('Unexpected id');
        }
        return data;
    }

    /**
     * Parses quest eventType, will throw an error if not valid
     */
    public static parseQuestEventType(data: unknown): EventType {
        if (isEventType(data)) return data;
        throw new Error('Unexpected event type');
    }

    /**
     * Parses quest expiration date, will throw an error if not valid
     */
    public static parseQuestExpiration(data: unknown): Date | null {
        if (data === null) return data as null;

        // if expiration is null or not a positive integer then throw error
        if (typeof data !== 'number' || Math.floor(data) !== data || data < 0)
            throw new Error('Inactive quest with unexpected cooldownStartedAt');

        return new Date(data);
    }

    public static isExpireableQuest(quest: IPlayerStateQuestData): quest is IPlayerExpireableQuestData {
        return !!(quest as IPlayerExpireableQuestData).expiration;
    }

    public static parseLastReward(data: unknown): number {
        if (typeof data !== 'number') {
            return undefined;
        }
        return data;
    }
}

const CACHED_QUEST_TYPES = cached<QuestType[]>(() => {
    return Object.values(Quests);
});
